import { useGetSpaceAvailability } from '@cocoonspace/sdk-js/domains/availabilities/availabilities'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useEventSimulation } from '@cocoonspace/shared/hooks/cocoon-api'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { useIsMutating } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { Button } from '~/components/ui/button'
import { BOOKING_KEYS } from '~/domains/booking/hooks/use-booking-find-or-create.hook'
import { useTracking } from '~/providers/tracking-provider'
import { BookingWeekdayPicker } from '../../atoms/booking-weekday-picker'
import type {
	BookingMode,
	BookingType,
	TimeSlot,
} from '../../templates/booking-stepper'
import { TimeScheduler } from '../time-scheduler'
import { FooterDateTime } from './footer-date-time'
import { FooterPriceCreate } from './footer-price-create'
import { FooterPriceUpdate } from './footer-price-update'
import { useRouterIsLoading } from '~/hooks/use-is-router-loading.hook'

export interface DetailsSidebarStepTimeProps {
	mode?: BookingMode
	type: BookingType
	value: TimeSlot
	event?: Event
	maxDate: string
	onChange: (value: TimeSlot) => void
	onSubmit: VoidFunction
	space: Space
	className?: string
}

export const DetailsSidebarStepTime = ({
	value,
	event,
	onChange,
	onSubmit,
	space,
	maxDate,
	className,
	type,
	mode = 'create',
}: DetailsSidebarStepTimeProps) => {
	const { t } = useTranslation()
	const { track } = useTracking()
	const bookingFindOrCreateIsLoading = useIsMutating({
		mutationKey: BOOKING_KEYS.findOrCreate(),
	})

	const isRouterLoading = useRouterIsLoading()

	const availabilityQuery = useGetSpaceAvailability(space.id, {
		day: value.day!,
		start: value.start!,
		end: value.end!,
		type: type as any,
	})

	const { data: bookingSimulation, isLoading: bookingSimulationIsLoading } =
		useEventSimulation(event?.id!, [value])

	const isAvailable = useMemo(() => {
		if (mode === 'update') {
			return bookingSimulationIsLoading ? true : !bookingSimulation?.[0].error
		} else {
			return availabilityQuery.isLoading
				? true
				: availabilityQuery.data?.data.available
		}
	}, [
		mode,
		bookingSimulationIsLoading,
		bookingSimulation,
		availabilityQuery.isLoading,
		availabilityQuery.data,
	])

	const notAvailableMsg = bookingSimulation?.[0].error

	return (
		<div className={className}>
			<div className='flex h-[60px] items-center justify-center font-bold text-[18px] capitalize'>
				{dateTimeUtils(value.day).format('dddd Do MMMM')}
			</div>

			<BookingWeekdayPicker
				min={dateTimeUtils().customFormat('dateTech')}
				max={maxDate}
				value={value.day!}
				className='mb-2'
				onChange={(day) => onChange({ ...value, day })}
			/>

			<div className='h-[380px] overflow-x-hidden overflow-y-scroll p-4'>
				<TimeScheduler
					value={value}
					spaceId={space.id}
					eventId={event?.id}
					isAvailable={isAvailable}
					notAvailableMsg={notAvailableMsg}
					onTimeChange={(start, end) => {
						track.timePickerSlotDrag()
						onChange({ ...value, start, end })
					}}
				/>
			</div>

			<hr className='border-slate-200' />

			<div className='flex min-h-[93px] flex-col p-4'>
				<FooterDateTime
					value={value}
					onChange={({ start, end }) => {
						track.timePickerSelectClick()

						return onChange({ ...value, start, end })
					}}
				/>

				{isAvailable && (
					<div className='flex items-center'>
						{mode === 'create' &&
							type === 'meeting' &&
							availabilityQuery.data && (
								<FooterPriceCreate availability={availabilityQuery.data.data} />
							)}

						{mode === 'update' && bookingSimulation?.length && event && (
							<FooterPriceUpdate
								bookingSimulation={bookingSimulation[0]}
								event={event}
							/>
						)}

						<div className='ml-auto'>
							<Button
								data-testid='book-space'
								variant='gradient'
								size='sm'
								disabled={bookingFindOrCreateIsLoading > 0 || isRouterLoading}
								onClick={onSubmit}
							>
								{mode === 'create' && type === 'meeting'
									? t('actions:book.btnLabel')
									: t('actions:chooseTimeSlot.btnLabel')}
							</Button>
						</div>
					</div>
				)}

				{!isAvailable && (
					<div className='min-h-[45px] text-sm'>
						{t('booking:stepper.priceFooter.unavailable')}
					</div>
				)}
			</div>
		</div>
	)
}
